/**
 * CreditCardRecon.tsx — Credit Card Reconciliation main page
 *
 * Access: POWER_USER or ADMIN role (enforced by RequireAuth in App.tsx)
 * Route:  /credit-card-upload
 *
 * Layout:
 *   - Sticky nav bar (matches existing app style)
 *   - Session list view (when no session active) + Upload button
 *   - Session detail view (when session active):
 *       · Session header card (name, period, totals, action buttons)
 *       · TransactionGrid (editable grid with tabs)
 *   - Upload modal (Chase CSV → createSession)
 *   - Page-level toast for upload, convert, complete operations
 *
 * State ownership:
 *   - CreditCardRecon owns sessions[], activeSession, transactions[]
 *   - CreditCardRecon handles: upload, convertRow, convertAll, complete
 *   - TransactionGrid handles: inline editing (updateTransaction API) internally
 *   - TransactionGrid calls onTransactionUpdated to propagate changes upward
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TransactionGrid from '../components/cc/TransactionGrid';
import PossibleMatchPanel from '../components/cc/PossibleMatchPanel';
import {
  listSessions,
  createSession,
  listCreditCards,
  getSession,
  convertTransaction,
  convertAll,
  completeSession,
  findSessionMatches,
  deleteSession,
  confirmMatch,
  runAiResolution,
  forceTransactionStatus,
  updateSessionFields,
  printReconReport,
} from '../api/ccReconApi';
import type {
  CCReconSession,
  CCReconTransaction,
  CCParseSummary,
  CreditCard,
} from '../types/ccTypes';
import { CC_REPORT_ONLY_STATUSES } from '../types/ccTypes';

const NAV_COLOR = '#00386C';

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtDollar(n: number | string): string {
  return '$' + Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  // Parse as local date to avoid UTC-offset display issues
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  const d1 = new Date(sy, sm - 1, sd);
  const d2 = new Date(ey, em - 1, ed);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${d1.toLocaleDateString('en-US', opts)} – ${d2.toLocaleDateString('en-US', opts)}`;
}

/** "YYYY-MM-DD" → "YYMM" for the CC{vendor}-{YYMM}-{seq} invoice prefix. */
function yymmOf(periodStart: string | null): string {
  if (!periodStart) return '----';
  const [y, m] = periodStart.split('-');
  if (!y || !m) return '----';
  return y.slice(2) + m;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CreditCardRecon() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Session list + active session
  const [sessions, setSessions]             = useState<CCReconSession[]>([]);
  const [activeSession, setActiveSession]   = useState<CCReconSession | null>(null);
  const [transactions, setTransactions]     = useState<CCReconTransaction[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingSession, setLoadingSession] = useState(false);

  // Action loading states
  const [convertingAll, setConvertingAll]         = useState(false);
  const [convertingSelected, setConvertingSelected] = useState(false);
  const [completing, setCompleting]               = useState(false);
  const [convertingIds, setConvertingIds]         = useState<Set<number>>(new Set());
  const [findingMatches, setFindingMatches]       = useState(false);
  const [deletingId, setDeletingId]               = useState<number | null>(null);
  const [reportLoading, setReportLoading]         = useState(false);

  // Reconciliation inputs (string-backed for free typing; saved on blur).
  // Keyed by the API field name they map to.
  const [beginBalInput, setBeginBalInput]         = useState('');
  const [endBalInput, setEndBalInput]             = useState('');
  const [financeInput, setFinanceInput]           = useState('');
  const [feeInput, setFeeInput]                   = useState('');
  const [seqInput, setSeqInput]                   = useState('');   // starting XXXX
  const [savingField, setSavingField]             = useState<string | null>(null);

  // Possible Match panel
  const [reviewTxn, setReviewTxn] = useState<CCReconTransaction | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Upload modal
  const [showUpload, setShowUpload]               = useState(false);
  const [uploadFile, setUploadFile]               = useState<File | null>(null);
  const [uploadName, setUploadName]               = useState('');
  const [uploading, setUploading]                 = useState(false);
  const [uploadError, setUploadError]             = useState<string | null>(null);
  const [, setParseSummary]                       = useState<CCParseSummary | null>(null);
  const [creditCards, setCreditCards]             = useState<CreditCard[]>([]);
  const [uploadCcVendorNumber, setUploadCcVendorNumber] = useState<number | null>(null);
  const [loadingCards, setLoadingCards]           = useState(false);
  const [uploadElapsed, setUploadElapsed]         = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI resolution state (Phase 2 — runs after upload, tiers 1-3 only in Phase 1)
  const [aiResolving, setAiResolving]             = useState(false);
  const [, setAiResolveResult]                    = useState<{ resolved: number; still_unresolved: number } | null>(null);

  // ── Toast helper ────────────────────────────────────────────────────────────
  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load sessions on mount ──────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const data = await listSessions();
      setSessions(data);
    } catch {
      showToast('Failed to load sessions', false);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ── Upload progress timer (increments every second while uploading) ────────
  useEffect(() => {
    if (!uploading) { setUploadElapsed(0); return; }
    const interval = setInterval(() => setUploadElapsed(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [uploading]);

  // ── Open a session ──────────────────────────────────────────────────────────
  const openSession = useCallback(async (id: number) => {
    setLoadingSession(true);
    try {
      const { session, transactions: txns } = await getSession(id);
      setActiveSession(session);
      setTransactions(txns);
    } catch {
      showToast('Failed to load session', false);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  // ── Back to session list ────────────────────────────────────────────────────
  const handleBack = () => {
    setActiveSession(null);
    setTransactions([]);
  };

  // ── Upload / create session — Phase 1: tiers 1-3 only (fast) ────────────────
  // Phase 2 (AI resolution for Unresolved rows) runs immediately after in handleUpload.
  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim() || !uploadCcVendorNumber) return;
    setUploading(true);
    setUploadError(null);
    setParseSummary(null);
    setAiResolveResult(null);
    try {
      // Phase 1 — parse CSV + run tiers 1-3 (DB-only, fast ~5s)
      const result = await createSession(uploadName.trim(), uploadFile, uploadCcVendorNumber);
      setSessions(prev => [result.session, ...prev]);
      setParseSummary(result.parse_summary);
      setUploadFile(null);
      setUploadName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setShowUpload(false);

      // Auto-open the new session (shows grid with tiers 1-3 results immediately)
      await openSession(result.session.id);

      showToast(
        `Uploaded — ${result.parse_summary.parsed} transactions loaded. Running AI resolution…`
      );

      // Phase 2 — run AI Assist (Tier 4) for any Unresolved rows
      // Runs in the background after the grid is visible; shows banner while in progress
      setAiResolving(true);
      try {
        const aiResult = await runAiResolution(result.session.id);
        setAiResolveResult({ resolved: aiResult.resolved, still_unresolved: aiResult.still_unresolved });

        // Reload transactions so the grid reflects newly AI-resolved rows
        await openSession(result.session.id);

        if (aiResult.resolved > 0) {
          showToast(
            `AI resolved ${aiResult.resolved} transaction${aiResult.resolved !== 1 ? 's' : ''}` +
            (aiResult.still_unresolved > 0 ? ` — ${aiResult.still_unresolved} still need manual coding` : ' — all done!')
          );
        } else if (aiResult.processed === 0) {
          showToast('All transactions resolved by rules — no AI calls needed');
        } else {
          showToast(
            `AI could not resolve ${aiResult.still_unresolved} transaction${aiResult.still_unresolved !== 1 ? 's' : ''} — manual coding required`,
            false
          );
        }
      } catch {
        showToast('AI resolution failed — Unresolved rows can be coded manually', false);
      } finally {
        setAiResolving(false);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Upload failed — check that the file is a valid Chase CSV';
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  // (Session 56: two-phase upload — Phase 1 tiers 1-3 fast, Phase 2 AI for Unresolved)

  const handleCloseUpload = () => {
    if (uploading) return;
    setShowUpload(false);
    setUploadError(null);
    setUploadFile(null);
    setUploadName('');
    setParseSummary(null);
    setUploadCcVendorNumber(null);
  };

  // ── Load credit cards when the upload modal opens ───────────────────────────
  useEffect(() => {
    if (!showUpload) return;
    setLoadingCards(true);
    listCreditCards()
      .then(cards => {
        // API already filters WHERE active = TRUE; trust it directly
        setCreditCards(cards);
        // Pre-select the default card if available
        const def = cards.find(c => c.is_default);
        if (def) setUploadCcVendorNumber(def.cc_vendor_number);
      })
      .catch(() => { /* non-fatal — user can still type manually if needed */ })
      .finally(() => setLoadingCards(false));
  }, [showUpload]);

  // ── Transaction updated (from grid inline edit) ─────────────────────────────
  const handleTransactionUpdated = useCallback((updated: CCReconTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
  }, []);

  // ── Per-row convert ─────────────────────────────────────────────────────────
  // Catches errors and shows toast — does NOT re-throw. Grid's onConvertRow
  // callback will see a resolved promise regardless.
  const handleConvertRow = useCallback(async (txnId: number) => {
    setConvertingIds(prev => new Set(prev).add(txnId));
    try {
      const updated = await convertTransaction(txnId);
      setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
      // Refresh session header to update totals
      if (activeSession) {
        try {
          const { session: refreshed } = await getSession(activeSession.id);
          setActiveSession(refreshed);
          setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
        } catch { /* non-fatal — totals slightly stale until next load */ }
      }
    } catch (err: any) {
      if (err?.response?.status === 501) {
        showToast('WinTeam posting not yet available — coming in a future update', false);
      } else {
        const msg = err?.response?.data?.error || 'Convert failed';
        showToast(msg, false);
      }
    } finally {
      setConvertingIds(prev => {
        const s = new Set(prev);
        s.delete(txnId);
        return s;
      });
    }
  }, [activeSession]);

  // ── Convert all eligible ────────────────────────────────────────────────────
  const handleConvertAll = async () => {
    if (!activeSession) return;
    setConvertingAll(true);
    try {
      const result = await convertAll(activeSession.id);
      const ok = result.converted.length;
      const fail = result.errors.length;
      showToast(
        ok > 0
          ? `Converted ${ok} transaction${ok !== 1 ? 's' : ''}${fail > 0 ? `, ${fail} error${fail !== 1 ? 's' : ''}` : ''}`
          : 'No eligible transactions to convert',
        fail === 0
      );
      // Full reload to get updated statuses + session totals
      const { session: refreshed, transactions: txns } = await getSession(activeSession.id);
      setActiveSession(refreshed);
      setTransactions(txns);
      setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
    } catch (err: any) {
      if (err?.response?.status === 501) {
        showToast('WinTeam posting not yet available — coming in a future update', false);
      } else {
        showToast(err?.response?.data?.error || 'Convert all failed', false);
      }
    } finally {
      setConvertingAll(false);
    }
  };

  // ── Convert selected rows ───────────────────────────────────────────────────
  // Converts each ID in sequence. All are added to convertingIds so the grid
  // shows per-row spinners. Errors are counted; shown in toast after all complete.
  const handleConvertSelected = useCallback(async (ids: number[]) => {
    if (ids.length === 0) return;
    setConvertingSelected(true);
    // Mark all selected rows as "converting" so the grid shows spinners
    setConvertingIds(prev => {
      const s = new Set(prev);
      ids.forEach(id => s.add(id));
      return s;
    });
    let succeeded = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const updated = await convertTransaction(id);
        setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
        succeeded++;
      } catch (err: any) {
        failed++;
      } finally {
        setConvertingIds(prev => {
          const s = new Set(prev);
          s.delete(id);
          return s;
        });
      }
    }
    // Reload session header to update totals
    if (activeSession) {
      try {
        const { session: refreshed, transactions: txns } = await getSession(activeSession.id);
        setActiveSession(refreshed);
        setTransactions(txns);
        setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
      } catch { /* non-fatal — totals slightly stale until next load */ }
    }
    showToast(
      succeeded > 0
        ? `Converted ${succeeded} transaction${succeeded !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}`
        : 'No transactions converted',
      failed === 0
    );
    setConvertingSelected(false);
  }, [activeSession]);

  // ── Complete session ────────────────────────────────────────────────────────
  const handleComplete = async () => {
    if (!activeSession) return;
    if (!window.confirm(
      `Mark "${activeSession.session_name}" as complete?\n\nYou can still convert and edit remaining Coded/Corrected transactions after completing.`
    )) return;
    setCompleting(true);
    try {
      const refreshed = await completeSession(activeSession.id);
      setActiveSession(refreshed);
      setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
      showToast('Session completed ✓');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Cannot complete session';
      showToast(msg, false);
    } finally {
      setCompleting(false);
    }
  };

  // ── Delete session ──────────────────────────────────────────────────────────
  const handleDeleteSession = async (s: CCReconSession, e: React.MouseEvent) => {
    e.stopPropagation(); // don't open the session row

    const hasConverted = Number(s.total_matched) > 0;
    const confirmMsg = hasConverted
      ? `Delete "${s.session_name}"?\n\nThis session has converted transactions — the WinTeam invoices will NOT be reversed.\n\nAll local session data will be permanently deleted.`
      : `Delete "${s.session_name}"?\n\nAll ${s.total_transactions} transaction${s.total_transactions !== 1 ? 's' : ''} will be permanently deleted.`;

    if (!window.confirm(confirmMsg)) return;

    setDeletingId(s.id);
    try {
      await deleteSession(s.id);
      setSessions(prev => prev.filter(sess => sess.id !== s.id));
      showToast(`Session "${s.session_name}" deleted`);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Delete failed';
      showToast(msg, false);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Find Matches (batch WinTeam search for the session) ────────────────────
  const handleFindMatches = async () => {
    if (!activeSession) return;
    setFindingMatches(true);
    try {
      const result = await findSessionMatches(activeSession.id);
      if (result.updated > 0) {
        showToast(
          `${result.updated} transaction${result.updated !== 1 ? 's' : ''} marked as Possible Match`
        );
        // Reload to show updated statuses
        const { session: refreshed, transactions: txns } = await getSession(activeSession.id);
        setActiveSession(refreshed);
        setTransactions(txns);
        setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
      } else {
        showToast(
          `No WinTeam matches found for ${result.checked} transaction${result.checked !== 1 ? 's' : ''}`,
          true
        );
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Match search failed', false);
    } finally {
      setFindingMatches(false);
    }
  };

  // ── Possible Match panel ────────────────────────────────────────────────────
  const handleReviewMatch = useCallback((txnId: number) => {
    const txn = transactions.find(t => t.id === txnId);
    if (txn) setReviewTxn(txn);
  }, [transactions]);

  const handleMatchConfirmed = useCallback((updated: CCReconTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
    setReviewTxn(null);
    showToast(`Confirmed match — ${updated.matched_invoice_number}`);
    // Refresh session totals non-blocking
    if (activeSession) {
      getSession(activeSession.id)
        .then(({ session: refreshed }) => {
          setActiveSession(refreshed);
          setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
        })
        .catch(() => { /* non-fatal */ });
    }
  }, [activeSession]);

  const handleMatchRejected = useCallback((updated: CCReconTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
    setReviewTxn(null);
    showToast(`Match rejected — returned to ${updated.status}`);
  }, []);

  // ── Manual match (user enters WinTeam invoice number) ─────────────────────
  const handleManualMatch = useCallback(async (txnId: number) => {
    const invoiceNumber = window.prompt('Enter the WinTeam invoice number to match:');
    if (!invoiceNumber || !invoiceNumber.trim()) return;

    try {
      const updated = await confirmMatch(txnId, invoiceNumber.trim());
      setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
      showToast(`Manual match confirmed — ${updated.matched_invoice_number}`);
      // Refresh session totals non-blocking
      if (activeSession) {
        getSession(activeSession.id)
          .then(({ session: refreshed }) => {
            setActiveSession(refreshed);
            setSessions(prev => prev.map(s => s.id === refreshed.id ? refreshed : s));
          })
          .catch(() => { /* non-fatal */ });
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Manual match failed', false);
    }
  }, [activeSession]);

  // ── Admin: Force-status handler ─────────────────────────────────────────────
  const handleForceStatus = useCallback(async (
    txnId: number,
    status: import('../types/ccTypes').CCTransactionStatus,
    notes?: string,
  ) => {
    try {
      const updated = await forceTransactionStatus(txnId, status, notes);
      setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
      showToast(`Status changed to ${updated.status}`);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Status change failed', false);
      throw err; // re-throw so modal knows to stop saving spinner
    }
  }, []);

  // ── Derived state ───────────────────────────────────────────────────────────
  const unresolvedCount     = transactions.filter(t => t.status === 'Unresolved').length;
  const errorCount          = transactions.filter(t => t.status === 'Error').length;
  const eligibleCount       = transactions.filter(t => t.status === 'Coded' || t.status === 'Corrected').length;
  const possibleMatchCount  = transactions.filter(t => t.status === 'Possible Match').length;
  const canComplete         = activeSession?.status === 'open' && unresolvedCount === 0 && errorCount === 0 && transactions.length > 0;

  // Payments, fees, and finance charges are report/totals-only — never shown
  // in the working grid.
  const convertedCount = transactions.filter(t => t.status === 'Converted').length;
  const visibleTransactions = useMemo(
    () => transactions.filter(t => !CC_REPORT_ONLY_STATUSES.includes(t.status)),
    [transactions]
  );

  // ── Reconciliation totals (drive the top totals panel + report parity) ───────
  // Finance charge + fee come from the editable boxes (auto-filled from the
  // parser), not the row sums, so a manual override flows straight to the math.
  const recon = useMemo(() => {
    const sum = (rows: CCReconTransaction[]) =>
      rows.reduce((acc, t) => acc + Number(t.invoice_amount || 0), 0);
    const chargesTotal  = sum(transactions.filter(t => t.type === 'Sale'));
    const creditsTotal  = sum(transactions.filter(t => t.type === 'Return'));
    const paymentsTotal = sum(transactions.filter(t => t.status === 'Payment'));

    const num = (s: string) => (s.trim() === '' ? null : Number(s));
    const beginNum = num(beginBalInput);
    const endNum   = num(endBalInput);
    const finNum   = num(financeInput) ?? 0;
    const feeNum   = num(feeInput) ?? 0;
    const beginValid = beginNum !== null && !Number.isNaN(beginNum);
    const endValid   = endNum   !== null && !Number.isNaN(endNum);

    const financeTotal = Number.isNaN(finNum) ? 0 : finNum;
    const feeTotal     = Number.isNaN(feeNum) ? 0 : feeNum;

    const computedEnding =
      (beginValid ? beginNum! : 0) + chargesTotal + financeTotal + feeTotal
      - creditsTotal - paymentsTotal;
    const diff = endValid ? endNum! - computedEnding : null;   // unreconciled amount
    const balances = beginValid && endValid && Math.abs(diff!) < 0.005;

    return { chargesTotal, creditsTotal, paymentsTotal, financeTotal, feeTotal,
             computedEnding, diff, balances, beginValid, endValid };
  }, [transactions, beginBalInput, endBalInput, financeInput, feeInput]);

  // Keep the inputs in sync with the loaded/active session
  useEffect(() => {
    const s = activeSession;
    setBeginBalInput(s?.beginning_balance != null ? String(s.beginning_balance) : '');
    setEndBalInput(s?.ending_balance != null ? String(s.ending_balance) : '');
    setFinanceInput(s?.finance_charge != null ? String(s.finance_charge) : '');
    setFeeInput(s?.fee_charged != null ? String(s.fee_charged) : '');
    setSeqInput(s?.invoice_seq_start != null ? String(s.invoice_seq_start) : '1');
  }, [activeSession?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save a reconciliation field (on blur) ────────────────────────────────────
  // apiField is the cc_recon_sessions column; `int` for invoice_seq_start.
  const saveField = useCallback(async (
    apiField: 'beginning_balance' | 'ending_balance' | 'finance_charge' | 'fee_charged' | 'invoice_seq_start',
    raw: string,
    opts: { int?: boolean } = {},
  ) => {
    if (!activeSession) return;
    const trimmed = (raw ?? '').trim();
    let value: number | null;
    if (opts.int) {
      value = trimmed === '' ? 1 : parseInt(trimmed, 10);
      if (value === null || Number.isNaN(value) || value < 1) {
        showToast('Starting invoice number must be a whole number ≥ 1', false);
        return;
      }
    } else {
      value = trimmed === '' ? null : Number(trimmed);
      if (value !== null && Number.isNaN(value)) {
        showToast('Value must be a number', false);
        return;
      }
    }
    const current = (activeSession as any)[apiField] ?? null;
    if ((current ?? null) === (value ?? null)) return;   // unchanged → skip

    try {
      setSavingField(apiField);
      const updated = await updateSessionFields(activeSession.id, { [apiField]: value });
      setActiveSession(updated);
      setSessions(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to save', false);
    } finally {
      setSavingField(null);
    }
  }, [activeSession]);

  // ── Generate the reconciliation PDF report ───────────────────────────────────
  const handleReport = useCallback(async () => {
    if (!activeSession) return;
    try {
      setReportLoading(true);
      const url = await printReconReport(activeSession.id);
      window.open(url, '_blank', 'noopener');
      // Revoke after a delay so the new tab has time to load the blob
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to generate report', false);
    } finally {
      setReportLoading(false);
    }
  }, [activeSession]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* ── Sticky nav bar ────────────────────────────────────────────────── */}
      <div style={{
        backgroundColor: NAV_COLOR,
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '52px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
            Credit Card Reconciliation
          </span>
          {activeSession && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '16px' }}>/</span>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
                {activeSession.session_name}
              </span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            onClick={() => navigate('/invoices')}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              padding: '4px 14px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            ← Invoice List
          </button>
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
            {user?.display_name}
          </span>
          <button
            onClick={logout}
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* ── Page content ──────────────────────────────────────────────────── */}
      <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '24px' }}>

        {/* ── SESSION LIST VIEW ─────────────────────────────────────────── */}
        {!activeSession && !loadingSession && (
          <>
            {/* Header row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1a1a1a', fontWeight: 700 }}>
                Reconciliation Sessions
              </h2>
              <button
                onClick={() => setShowUpload(true)}
                style={{
                  backgroundColor: NAV_COLOR,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                + Upload Chase CSV
              </button>
            </div>

            {loadingSessions ? (
              <div style={{ textAlign: 'center', padding: '80px', color: '#999', fontSize: '15px' }}>
                Loading sessions…
              </div>
            ) : sessions.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '80px 24px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                color: '#999',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>💳</div>
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#666' }}>
                  No reconciliation sessions yet
                </div>
                <div style={{ fontSize: '13px', marginBottom: '24px' }}>
                  Upload a Chase CSV statement to auto-code and post transactions to WinTeam
                </div>
                <button
                  onClick={() => setShowUpload(true)}
                  style={{
                    backgroundColor: NAV_COLOR,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 22px',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                >
                  + Upload Chase CSV
                </button>
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}>
                      {['Session Name', 'Period', 'Transactions', 'Converted', 'Remaining', 'Status', 'Uploaded', ''].map(h => (
                        <th
                          key={h}
                          style={{
                            padding: '10px 14px',
                            textAlign: ['Transactions', 'Converted', 'Remaining'].includes(h) ? 'right' : 'left',
                            fontWeight: 600,
                            color: '#555',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map(s => (
                      <tr
                        key={s.id}
                        onClick={() => s.status !== 'processing' && openSession(s.id)}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          cursor: s.status === 'processing' ? 'default' : 'pointer',
                          transition: 'background-color 0.1s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9ff')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                      >
                        <td style={{ padding: '11px 14px', fontWeight: 600, color: NAV_COLOR }}>
                          {s.session_name}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#444' }}>
                          {fmtPeriod(s.period_start, s.period_end)}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#444', textAlign: 'right' }}>
                          {s.total_transactions}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#2d7a2d', fontWeight: 500, textAlign: 'right' }}>
                          {fmtDollar(s.total_matched)}
                        </td>
                        <td style={{
                          padding: '11px 14px',
                          textAlign: 'right',
                          fontWeight: 500,
                          color: Number(s.total_unmatched) > 0 ? '#b8860b' : '#2d7a2d',
                        }}>
                          {fmtDollar(s.total_unmatched)}
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '10px',
                            backgroundColor:
                              s.status === 'complete'    ? '#e8f5e9' :
                              s.status === 'processing'  ? '#e3f2fd' :
                              s.status === 'error'       ? '#fdecea' :
                              '#fff8e1',
                            color:
                              s.status === 'complete'    ? '#1b5e20' :
                              s.status === 'processing'  ? '#1565c0' :
                              s.status === 'error'       ? '#c0392b' :
                              '#b8860b',
                          }}>
                            {s.status === 'complete'   ? '✓ Complete' :
                             s.status === 'processing' ? '⟳ Processing…' :
                             s.status === 'error'      ? '✗ Error' :
                             '● Open'}
                          </span>
                        </td>
                        <td style={{ padding: '11px 14px', color: '#888', whiteSpace: 'nowrap', fontSize: '12px' }}>
                          {new Date(s.uploaded_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <button
                              onClick={e => { e.stopPropagation(); if (s.status !== 'processing') openSession(s.id); }}
                              disabled={s.status === 'processing'}
                              style={{
                                padding: '4px 12px',
                                fontSize: '12px',
                                backgroundColor: s.status === 'processing' ? '#e0e0e0' : '#f0f2f5',
                                border: '1px solid #cbd5e0',
                                borderRadius: '4px',
                                cursor: s.status === 'processing' ? 'default' : 'pointer',
                                fontWeight: 600,
                                color: s.status === 'processing' ? '#888' : NAV_COLOR,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {s.status === 'processing' ? 'Processing…' : 'Open →'}
                            </button>
                            {s.status !== 'complete' && (
                              <button
                                onClick={e => handleDeleteSession(s, e)}
                                disabled={deletingId === s.id}
                                title="Delete this session and all its transactions"
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '12px',
                                  backgroundColor: deletingId === s.id ? '#f5f5f5' : '#fff0f0',
                                  border: `1px solid ${deletingId === s.id ? '#ddd' : '#f5c6cb'}`,
                                  borderRadius: '4px',
                                  cursor: deletingId === s.id ? 'default' : 'pointer',
                                  fontWeight: 600,
                                  color: deletingId === s.id ? '#aaa' : '#c0392b',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {deletingId === s.id ? '…' : '🗑'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── SESSION LOADING SPINNER ───────────────────────────────────── */}
        {loadingSession && (
          <div style={{ textAlign: 'center', padding: '80px', color: '#999', fontSize: '15px' }}>
            Loading session…
          </div>
        )}

        {/* ── ACTIVE SESSION VIEW ───────────────────────────────────────── */}
        {activeSession && !loadingSession && (
          <>
            {/* Back link */}
            <button
              onClick={handleBack}
              style={{
                background: 'none',
                border: 'none',
                color: NAV_COLOR,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                padding: '0 0 14px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              ← All Sessions
            </button>

            {/* AI Resolution banner — visible while Phase 2 (Tier 4) is running */}
            {aiResolving && (
              <div style={{
                backgroundColor: '#e8f4fd',
                border: '1px solid #b3d7f5',
                borderRadius: '6px',
                padding: '10px 16px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '13px',
                color: '#1a5f8a',
              }}>
                <span style={{ fontSize: '16px' }}>⟳</span>
                <span><strong>AI resolution in progress</strong> — coding Unresolved transactions with Claude. This may take 30–90 seconds. You can browse the grid while this runs.</span>
              </div>
            )}

            {/* Session header card */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px 24px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              marginBottom: '16px',
            }}>
              {/* Name + status badge */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '14px',
              }}>
                <div>
                  <h2 style={{ margin: '0 0 4px', fontSize: '18px', color: '#1a1a1a', fontWeight: 700 }}>
                    {activeSession.session_name}
                  </h2>
                  <div style={{ color: '#666', fontSize: '13px' }}>
                    Period: {fmtPeriod(activeSession.period_start, activeSession.period_end)}
                    <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                    Uploaded by {activeSession.uploaded_by}
                  </div>
                </div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: '12px',
                  backgroundColor: activeSession.status === 'complete' ? '#e8f5e9' : '#fff8e1',
                  color: activeSession.status === 'complete' ? '#1b5e20' : '#b8860b',
                  whiteSpace: 'nowrap',
                }}>
                  {activeSession.status === 'complete' ? '✓ Complete' : '● Open'}
                </span>
              </div>

              {/* Totals strip */}
              <div style={{
                display: 'flex',
                gap: '32px',
                flexWrap: 'wrap',
                paddingBottom: activeSession.status === 'open' ? '16px' : '0',
                borderBottom: activeSession.status === 'open' ? '1px solid #f0f0f0' : 'none',
                marginBottom: activeSession.status === 'open' ? '16px' : '0',
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Transactions
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#1a1a1a' }}>
                    {activeSession.total_transactions}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Converted
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#2d7a2d' }}>
                    {fmtDollar(activeSession.total_matched)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Remaining
                  </div>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    color: Number(activeSession.total_unmatched) > 0 ? '#b8860b' : '#2d7a2d',
                  }}>
                    {fmtDollar(activeSession.total_unmatched)}
                  </div>
                </div>
                {unresolvedCount > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#b8860b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      ⚠ Unresolved
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#b8860b' }}>
                      {unresolvedCount}
                    </div>
                  </div>
                )}
                {errorCount > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#c0392b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      ⚠ Errors
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#c0392b' }}>
                      {errorCount}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Reconciliation panel (open & complete) ──────────────────── */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '14px', marginBottom: '16px' }}>

                {/* Row 1 — read-only section totals + Report button */}
                <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '14px' }}>
                  {([
                    ['Charges',         recon.chargesTotal],
                    ['Finance Charges', recon.financeTotal],
                    ['Fees',            recon.feeTotal],
                    ['Credits',         recon.creditsTotal],
                    ['Payments',        recon.paymentsTotal],
                  ] as Array<[string, number]>).map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a' }}>
                        {fmtDollar(val)}
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={handleReport}
                    disabled={reportLoading}
                    title="Generate the reconciliation PDF report"
                    style={{
                      marginLeft: 'auto',
                      padding: '8px 18px',
                      fontSize: '13px',
                      fontWeight: 700,
                      backgroundColor: reportLoading ? '#e0e0e0' : '#fff',
                      color: reportLoading ? '#888' : NAV_COLOR,
                      border: `1px solid ${reportLoading ? '#ccc' : NAV_COLOR}`,
                      borderRadius: '6px',
                      cursor: reportLoading ? 'default' : 'pointer',
                    }}
                  >
                    {reportLoading ? 'Generating…' : '🧾 PDF Report'}
                  </button>
                </div>

                {/* Row 2 — editable balance inputs + starting invoice # + verdict */}
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {/* Starting invoice number (prefix + editable 4-digit sequence) */}
                  <div>
                    <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
                      Starting Invoice #{savingField === 'invoice_seq_start' ? ' …' : ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#555', fontFamily: 'monospace' }}>
                        {`CC${activeSession.cc_vendor_number ?? 161}-${yymmOf(activeSession.period_start)}-`}
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={seqInput}
                        onChange={e => setSeqInput(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={() => saveField('invoice_seq_start', seqInput, { int: true })}
                        disabled={convertedCount > 0}
                        title={convertedCount > 0 ? 'Locked — transactions already converted in this session' : 'Starting sequence number for converted invoices'}
                        style={{
                          width: '70px', padding: '5px 8px', fontSize: '15px', fontWeight: 600,
                          border: '1px solid #cbd5e0', borderRadius: '5px', textAlign: 'right',
                          fontFamily: 'monospace',
                          backgroundColor: convertedCount > 0 ? '#f0f0f0' : 'white',
                          color: convertedCount > 0 ? '#999' : '#1a1a1a',
                        }}
                      />
                    </div>
                  </div>

                  {/* Money inputs: beginning, ending, finance charge, fee */}
                  {([
                    { field: 'beginning_balance' as const, label: 'Beginning Balance', val: beginBalInput, set: setBeginBalInput },
                    { field: 'ending_balance'    as const, label: 'Ending Balance',    val: endBalInput,   set: setEndBalInput },
                    { field: 'finance_charge'    as const, label: 'Finance Charge',    val: financeInput,  set: setFinanceInput },
                    { field: 'fee_charged'       as const, label: 'Fee Charged',       val: feeInput,      set: setFeeInput },
                  ]).map(f => (
                    <div key={f.field}>
                      <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
                        {f.label}{savingField === f.field ? ' …' : ''}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '15px', color: '#888', marginRight: '2px' }}>$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={f.val}
                          onChange={e => f.set(e.target.value)}
                          onBlur={() => saveField(f.field, f.val)}
                          placeholder="0.00"
                          style={{
                            width: '100px', padding: '5px 8px', fontSize: '15px', fontWeight: 600,
                            border: '1px solid #cbd5e0', borderRadius: '5px', textAlign: 'right',
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Computed ending */}
                  <div>
                    <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Computed Ending
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a' }}>
                      {fmtDollar(recon.computedEnding)}
                    </div>
                  </div>

                  {/* Balance verdict / unreconciled amount */}
                  <div style={{
                    padding: '6px 14px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    backgroundColor: !recon.beginValid || !recon.endValid ? '#f0f2f5'
                      : recon.balances ? '#e8f5e9' : '#fdecea',
                    color: !recon.beginValid || !recon.endValid ? '#888'
                      : recon.balances ? '#1b5e20' : '#b71c1c',
                  }}>
                    {!recon.beginValid || !recon.endValid
                      ? 'Enter balances to validate'
                      : recon.balances
                        ? '✓ Balances'
                        : `✗ Unreconciled: ${fmtDollar(Math.abs(recon.diff!))}`}
                  </div>
                </div>
              </div>

              {/* Action buttons (open sessions only) */}
              {activeSession.status === 'open' && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={() => setShowUpload(true)}
                    style={{
                      padding: '7px 16px',
                      fontSize: '13px',
                      fontWeight: 600,
                      backgroundColor: '#f0f2f5',
                      border: '1px solid #cbd5e0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      color: '#444',
                    }}
                  >
                    + Upload New Session
                  </button>

                  <button
                    onClick={handleFindMatches}
                    disabled={findingMatches}
                    title="Search WinTeam for already-entered invoices that match these transactions"
                    style={{
                      padding: '7px 16px',
                      fontSize: '13px',
                      fontWeight: 600,
                      backgroundColor: findingMatches ? '#e0e0e0' : '#fff3e0',
                      color: findingMatches ? '#888' : '#e65100',
                      border: `1px solid ${findingMatches ? '#ccc' : '#ffb74d'}`,
                      borderRadius: '6px',
                      cursor: findingMatches ? 'default' : 'pointer',
                    }}
                  >
                    {findingMatches
                      ? 'Searching…'
                      : possibleMatchCount > 0
                        ? `🔍 Find Matches (${possibleMatchCount} found)`
                        : '🔍 Find Matches'}
                  </button>

                  <button
                    onClick={handleConvertAll}
                    disabled={convertingAll || eligibleCount === 0}
                    title={eligibleCount === 0 ? 'No Coded or Corrected transactions to convert' : `Convert ${eligibleCount} eligible transaction${eligibleCount !== 1 ? 's' : ''}`}
                    style={{
                      padding: '7px 16px',
                      fontSize: '13px',
                      fontWeight: 700,
                      backgroundColor: (convertingAll || eligibleCount === 0) ? '#e0e0e0' : NAV_COLOR,
                      color: (convertingAll || eligibleCount === 0) ? '#888' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (convertingAll || eligibleCount === 0) ? 'default' : 'pointer',
                    }}
                  >
                    {convertingAll
                      ? 'Converting…'
                      : eligibleCount > 0
                        ? `⬆ Convert All Eligible (${eligibleCount})`
                        : '⬆ Convert All Eligible'}
                  </button>

                  <button
                    onClick={handleComplete}
                    disabled={!canComplete || completing}
                    title={
                      !canComplete
                        ? unresolvedCount > 0
                          ? `Resolve ${unresolvedCount} Unresolved row${unresolvedCount !== 1 ? 's' : ''} first`
                          : errorCount > 0
                            ? `Retry or clear ${errorCount} Error row${errorCount !== 1 ? 's' : ''} first`
                            : transactions.length === 0
                              ? 'No transactions in session'
                              : 'Session already complete'
                        : 'Close session — you can still convert remaining transactions'
                    }
                    style={{
                      padding: '7px 16px',
                      fontSize: '13px',
                      fontWeight: 700,
                      backgroundColor: (canComplete && !completing) ? '#2d7a2d' : '#e0e0e0',
                      color: (canComplete && !completing) ? 'white' : '#888',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (canComplete && !completing) ? 'pointer' : 'default',
                    }}
                  >
                    {completing ? 'Completing…' : '✓ Complete Session'}
                  </button>

                  {!canComplete && (unresolvedCount > 0 || errorCount > 0) && (
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      {[
                        unresolvedCount > 0 && `${unresolvedCount} unresolved`,
                        errorCount > 0 && `${errorCount} error${errorCount !== 1 ? 's' : ''}`,
                      ].filter(Boolean).join(', ')} must be resolved before completing
                    </span>
                  )}
                </div>
              )}

              {/* Complete message (locked sessions) */}
              {activeSession.status === 'complete' && activeSession.completed_at && (
                <div style={{ fontSize: '13px', color: '#2d7a2d', fontWeight: 600, marginTop: '4px' }}>
                  ✓ Completed {fmtDateTime(activeSession.completed_at)}
                </div>
              )}
            </div>

            {/* Transaction grid */}
            <TransactionGrid
              session={activeSession}
              transactions={visibleTransactions}
              onTransactionUpdated={handleTransactionUpdated}
              onConvertRow={handleConvertRow}
              convertingIds={convertingIds}
              onConvertSelected={handleConvertSelected}
              convertingSelected={convertingSelected}
              onReviewMatch={handleReviewMatch}
              onManualMatch={handleManualMatch}
              isAdmin={user?.role === 'ADMIN'}
              onForceStatus={user?.role === 'ADMIN' ? handleForceStatus : undefined}
            />
          </>
        )}
      </div>

      {/* ── Possible Match Panel ──────────────────────────────────────────── */}
      {reviewTxn && (
        <PossibleMatchPanel
          transaction={reviewTxn}
          onConfirmed={handleMatchConfirmed}
          onRejected={handleMatchRejected}
          onClose={() => setReviewTxn(null)}
        />
      )}

      {/* ── Upload Modal ──────────────────────────────────────────────────── */}
      {showUpload && (
        <div
          onClick={e => { if (e.target === e.currentTarget) handleCloseUpload(); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
          }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '28px',
            width: '500px',
            maxWidth: '95vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '17px', color: '#1a1a1a', fontWeight: 700 }}>
              Upload Chase CSV
            </h3>

            {/* Session name */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '5px' }}>
              Session Name
            </label>
            <input
              type="text"
              value={uploadName}
              onChange={e => setUploadName(e.target.value)}
              placeholder="e.g. Chase Feb 2026"
              disabled={uploading}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                marginBottom: '18px',
                outline: 'none',
              }}
            />

            {/* Credit card selector */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '5px' }}>
              Credit Card
            </label>
            {loadingCards ? (
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '18px' }}>Loading cards…</div>
            ) : (
              <select
                value={uploadCcVendorNumber ?? ''}
                onChange={e => setUploadCcVendorNumber(e.target.value ? Number(e.target.value) : null)}
                disabled={uploading}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  border: `1px solid ${!uploadCcVendorNumber ? '#e57373' : '#cbd5e0'}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  marginBottom: '18px',
                  backgroundColor: 'white',
                  outline: 'none',
                  color: uploadCcVendorNumber ? '#1a1a1a' : '#888',
                }}
              >
                <option value="">— Select a credit card —</option>
                {creditCards.map(card => (
                  <option key={card.id} value={card.cc_vendor_number}>
                    {card.description}{card.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            )}

            {/* File picker */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '5px' }}>
              Chase CSV File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              disabled={uploading}
              onChange={e => setUploadFile(e.target.files?.[0] || null)}
              style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}
            />
            {uploadFile ? (
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
                {uploadFile.name} — {(uploadFile.size / 1024).toFixed(1)} KB
              </div>
            ) : (
              <div style={{ marginBottom: '16px' }} />
            )}

            {/* Error */}
            {uploadError && (
              <div style={{
                color: '#c0392b',
                fontSize: '13px',
                marginBottom: '14px',
                padding: '9px 12px',
                backgroundColor: '#fdecea',
                borderRadius: '4px',
              }}>
                {uploadError}
              </div>
            )}

            {/* Uploading progress indicator */}
            {uploading && (() => {
              // Stage labels based on elapsed time — gives the user a sense of
              // what the backend is doing even though we can't stream progress.
              const stages = [
                { at: 0,  label: 'Uploading file…' },
                { at: 3,  label: 'Parsing CSV rows…' },
                { at: 8,  label: 'Running CC Helper auto-resolution…' },
                { at: 15, label: 'Querying vendor matches…' },
                { at: 25, label: 'Running AI fallback for unresolved rows…' },
                { at: 40, label: 'Saving transactions to database…' },
                { at: 55, label: 'Still working — large files take longer…' },
              ];
              const stage = [...stages].reverse().find(s => uploadElapsed >= s.at) || stages[0];
              // Simulated progress: asymptotic approach to 95% (never reaches 100
              // until the response actually comes back)
              const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-uploadElapsed / 30))));
              return (
                <div style={{
                  marginBottom: '14px',
                  padding: '10px 12px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#1565c0', fontSize: '13px', fontWeight: 500 }}>
                      {stage.label}
                    </span>
                    <span style={{ color: '#1565c0', fontSize: '12px' }}>
                      {uploadElapsed}s elapsed
                    </span>
                  </div>
                  <div style={{
                    height: '6px',
                    backgroundColor: '#bbdefb',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      backgroundColor: '#1565c0',
                      borderRadius: '3px',
                      transition: 'width 1s ease-out',
                    }} />
                  </div>
                </div>
              );
            })()}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
              <button
                onClick={handleCloseUpload}
                disabled={uploading}
                style={{
                  padding: '8px 18px',
                  fontSize: '13px',
                  borderRadius: '6px',
                  cursor: uploading ? 'default' : 'pointer',
                  border: '1px solid #cbd5e0',
                  backgroundColor: 'white',
                  color: '#444',
                  fontWeight: 600,
                  opacity: uploading ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || !uploadName.trim() || !uploadCcVendorNumber || uploading}
                style={{
                  padding: '8px 22px',
                  fontSize: '13px',
                  fontWeight: 700,
                  borderRadius: '6px',
                  border: 'none',
                  cursor: (!uploadFile || !uploadName.trim() || !uploadCcVendorNumber || uploading) ? 'default' : 'pointer',
                  backgroundColor: (!uploadFile || !uploadName.trim() || !uploadCcVendorNumber || uploading) ? '#e0e0e0' : NAV_COLOR,
                  color: (!uploadFile || !uploadName.trim() || !uploadCcVendorNumber || uploading) ? '#888' : 'white',
                }}
              >
                {uploading ? 'Parsing & resolving…' : 'Upload & Parse'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page toast ────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: toast.ok ? '#2d7a2d' : '#c0392b',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '6px',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
          maxWidth: '480px',
          textAlign: 'center',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
